import * as React from "react";
import { ConfirmModal } from "@teamprevent/common/confirmedButton/ConfirmModal";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { Button, Form, Loader, Modal } from "semantic-ui-react";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { useForm } from "react-hook-form";

import { isSystemAdminUser } from "@teamprevent/common/auth/roles";

import { requestReferralReportEdit } from "../../referralReport/actions";
import { ApplicationState } from "../../applicationState";

interface Props {
    task: TaskModel;
}

interface EditReportModal {
    reasonForEditingReport: string;
}

export const RequestReportEditButton = ({ task }: Props) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const reportEditAllowed = isSystemAdminUser(currentUser) && task.completed;

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState<boolean>(false);
    const [isReasonModalOpen, setIsReasonModalOpen] = React.useState<boolean>(false);

    const { register, handleSubmit, formState: { isSubmitting } } = useForm<EditReportModal>({
        shouldUseNativeValidation: true,
        defaultValues: { reasonForEditingReport: "" }
    });

    const openConfirmationModal = React.useCallback(() => setIsConfirmationModalOpen(true), []);
    const closeConfirmationModal = React.useCallback(() => setIsConfirmationModalOpen(false), []);
    const onConfirmEditModal = React.useCallback(() => { setIsReasonModalOpen(true); }, []);

    const closeReasonModal = React.useCallback(() => setIsReasonModalOpen(false), []);

    const onReasonSubmit = handleSubmit(async (model: EditReportModal) => {
        await dispatch(requestReferralReportEdit(task.caseId, task.id, model.reasonForEditingReport));
        setIsReasonModalOpen(false);
    });

    if (!reportEditAllowed) {
        return null;
    }

    return (
        <>
            <Button
                circular
                icon="edit"
                secondary
                size="mini"
                title="Edit Report"
                onClick={openConfirmationModal}
            />
            <ConfirmModal
                isOpen={isConfirmationModalOpen}
                /* eslint-disable max-len */
                promptText={`You should only edit this report for ${task.employeeRecord.name}, which was completed on ${task.completedDate?.format("L")} if you have been made aware of factual inaccuracies which need to be changed.\n
                            Editing this report will remove access to the previous report from the manager / employee portal immediately but the report will remain on file for a full audit trail and for any future SAR.\n
                            If you wish to edit this report, please type 'EDIT' in the box below and click Continue.\n\n`}
                /* eslint-enable max-len */
                headingText="Request Report Edit"
                typeToConfirmText="EDIT"
                confirmText="Continue"
                onConfirm={onConfirmEditModal}
                onClose={closeConfirmationModal}
            />
            <Modal as={Form} onSubmit={onReasonSubmit} open={isReasonModalOpen} onClose={closeReasonModal}>
                <Modal.Header content="Reason for Editing Report" />
                <Modal.Content>
                    <textarea {...register("reasonForEditingReport", {
                        required: "Please enter a reason for editing the report"
                    })} />
                    {isSubmitting && <Loader active />}
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={closeReasonModal} />
                    <Button className="warning" content="Continue" type="submit" disabled={isSubmitting} />
                </Modal.Actions>
            </Modal>
        </>
    );
};
